// mui
import {
  Box,
  Button,
  Card,
  CardContent,
  Stack,
  Typography,
} from '@mui/material';

// components
import { ModalPageTemplate } from '@/pwa/templates/ModalPageTemplate/ModalPageTemplate';
import { CareProviderCard } from '@/pwa/molecules/CareProviderCard/CareProviderCard';

// models
import { ShortlistItem, SHORTLIST_ITEM_STATUS } from '@karehero/models';

export interface ShortlistDetailPageProps {
  shortlistName: string;
  subtitle: string;
  items: ShortlistItem[];
  onProviderClick: (item: ShortlistItem) => void;
  onMarkSecured: (item: ShortlistItem) => void;
  onBack: () => void;
  onSecuredElsewhere: () => void;
}

const ExpertNote = ({ note }: { note: string }) => (
  <Card variant='outlined'>
    <CardContent>
      <Stack spacing={0.5}>
        <Typography
          variant='caption'
          sx={{
            color: 'text.secondary',
          }}
        >
          Note from your care expert
        </Typography>
        <Typography variant='body2'>{note}</Typography>
      </Stack>
    </CardContent>
  </Card>
);

const SecuredButton = ({
  isSecured,
  onClick,
}: {
  isSecured: boolean;
  onClick: () => void;
}) => (
  <Button
    variant='outlined'
    fullWidth
    onClick={(e) => {
      e.stopPropagation();
      onClick();
    }}
  >
    {isSecured ? 'Mark as not secured' : 'Mark as secured'}
  </Button>
);

export const ShortlistDetailPage = ({
  shortlistName,
  subtitle,
  items,
  onProviderClick,
  onMarkSecured,
  onBack,
  onSecuredElsewhere,
}: ShortlistDetailPageProps) => {
  return (
    <ModalPageTemplate onBack={onBack}>
      <Stack
        spacing={2}
        sx={{
          maxWidth: 720,
          mx: 'auto',
          p: { xs: 2, sm: 3 },
        }}
      >
        <Stack spacing={0.5}>
          <Typography variant='h3'>{shortlistName}</Typography>
          <Typography
            variant='body2'
            sx={{
              color: 'text.secondary',
            }}
          >
            {subtitle}
          </Typography>
        </Stack>

        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          }}
        >
          {items.map((item) => {
            const isSecured = item.status === SHORTLIST_ITEM_STATUS.SECURED;

            return (
              <CareProviderCard
                key={item.regulatoryId}
                provider={item.provider}
                status={isSecured ? 'Secured' : undefined}
                onClick={() => onProviderClick(item)}
                notes={item.note ? <ExpertNote note={item.note} /> : undefined}
                actionButton={
                  <SecuredButton
                    isSecured={isSecured}
                    onClick={() => onMarkSecured(item)}
                  />
                }
              />
            );
          })}
        </Box>

        <Button
          variant='contained'
          fullWidth
          onClick={onSecuredElsewhere}
          size='large'
        >
          I've secured care elsewhere
        </Button>
      </Stack>
    </ModalPageTemplate>
  );
};

ShortlistDetailPage.displayName = 'ShortlistDetailPage';

export default ShortlistDetailPage;
